import React, { useEffect, useState } from 'react';
import { useRAGStore } from '../store/ragStore';
import { AskRequest, AskResponse, PromptBaseData, PromptData } from '../types/rag';
import { MessageCircle, RefreshCw, Globe, FileText, Clock, Edit3, Send } from 'lucide-react';
import ErrorDisplay from './ErrorDisplay';

const AskTab: React.FC = () => {
  const {
    currentContext,
    refreshCurrentContext,
    isOperationInProgress,
  } = useRAGStore();

  const [queryText, setQueryText] = useState('');
  // Shared retrieval params (store-backed; also editable in the Retrieval Settings card).
  const { retrievalParams, setRetrievalParams } = useRAGStore();
  const { topK, alpha, scoreThreshold, fileRetrievalK, contextWindow, useReranker } = retrievalParams;
  const setTopK = (v: number) => setRetrievalParams({ topK: v });
  const setAlpha = (v: number) => setRetrievalParams({ alpha: v });
  const setScoreThreshold = (v: number) => setRetrievalParams({ scoreThreshold: v });
  const setFileRetrievalK = (v: number) => setRetrievalParams({ fileRetrievalK: v });
  const setContextWindow = (v: number) => setRetrievalParams({ contextWindow: v });
  const setUseReranker = (v: boolean) => setRetrievalParams({ useReranker: v });
  const [fileSimilarityThreshold, setFileSimilarityThreshold] = useState(0.2);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [askResponse, setAskResponse] = useState<AskResponse | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  
  // New state for two-step process
  const [promptData, setPromptData] = useState<PromptData | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isSubmittingPrompt, setIsSubmittingPrompt] = useState(false);
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string>("");

  const [promptBaseData, setPromptBaseData] = useState<PromptBaseData | null>(null);
  const [editedSystemPrompt, setEditedSystemPrompt] = useState('');;
  const [editedNoContent, setEditedNoContent] = useState('');



  const handleGetPromptBase = async () => {
    if (!currentContext) return;

    setError(null);
    setIsGeneratingPrompt(true);
    setPromptBaseData(null);
    setShowPromptEditor(false);
    setAskResponse(null);
    setDuration(null);

    try {
      const { default: RAGApiService } = await import('../services/ragApiService');
      const apiService = new RAGApiService();

      const baseResponse: PromptBaseData = await apiService.get_prompt(currentContext.contextId);

      setPromptBaseData(baseResponse);
      setEditedSystemPrompt(baseResponse.system_prompt);
      setEditedNoContent(baseResponse.no_content);
      setShowPromptEditor(true);
    } catch (error) {
      let errorMessage = 'Failed to get prompt';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      setError(errorMessage);
      console.error('Get prompt error details:', error);
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handleSubmitPrompt = async () => {
    if (!currentContext) return;

    setError(null);
    setSubmitStatus(null);
    setIsSubmittingPrompt(true);
    setDuration(null);

    const startTime = Date.now();

    try {
      const { default: RAGApiService } = await import('../services/ragApiService');
      const apiService = new RAGApiService();

      const editedPromptBase: PromptBaseData = {
        system_prompt: editedSystemPrompt.trim(),
        no_content: editedNoContent.trim(),
      };

      const response = await apiService.upload_prompt(
        currentContext.contextId,
        editedPromptBase
      );

      setDuration(Date.now() - startTime);

      // Set state on success
      setSubmitStatus("success");
      setSubmitMessage("Prompt uploaded successfully!");
    } catch (error) {
      let errorMessage = "Failed to submit prompt";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = (error as any).message;
      }

      setError(errorMessage);

      // Set state on failure
      setSubmitStatus("error");
      setSubmitMessage(errorMessage);

      console.error("Submit prompt error details:", error);
    } finally {
      setIsSubmittingPrompt(false);
    }
  };

  const handleAsk = async () => {
    if (!currentContext || !queryText.trim()) return;

    setError(null);
    setAskResponse(null);
    setDuration(null);
    setIsLoading(true);

    const startTime = Date.now();

    try {
      const { default: RAGApiService } = await import('../services/ragApiService');
      const apiService = new RAGApiService();

      const request: AskRequest = {
        query: queryText.trim(),
        top_k: topK,
        alpha: alpha,
        score_threshold: scoreThreshold,
        file_retrieval_k: fileRetrievalK,
        file_similarity_threshold: fileSimilarityThreshold,
        context_window: contextWindow,
        use_reranker: useReranker
      };

      const response: AskResponse = await apiService.askSimple(
        currentContext.contextId,
        request
      );

      setAskResponse(response);
      setDuration(Date.now() - startTime);
    } catch (error) {
      let errorMessage = "Failed to ask";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = (error as any).message;
      }
      setError(errorMessage);
      console.error("Ask error details:", error);
    } finally {
      setIsLoading(false);
    }
  };



  const handleReset = () => {
    setPromptData(null);
    setEditedPrompt('');
    setShowPromptEditor(false);
    setAskResponse(null);
    setError(null);
    setDuration(null);
  };

  const getLanguageDisplayName = (language: string) => {
    const languageMap: Record<string, string> = {
      'zh': 'Chinese',
      'en': 'English',
      'ja': '日本語',
      'ko': '한국어'
    };
    return languageMap[language] || language;
  };

  const getLanguageFlag = (language: string) => {
    const flagMap: Record<string, string> = {
      'zh': '🇨🇳',
      'en': '🇺🇸',
      'ja': '🇯🇵',
      'ko': '🇰🇷'
    };
    return flagMap[language] || '🌐';
  };

  useEffect(() => {
    if (currentContext) {
      handleGetPromptBase();
    }
  }, [currentContext]);

  if (!currentContext) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Context Selected
          </h3>
          <p className="text-gray-600">
            Please go to the Context Management tab to select a context before using the Ask feature.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Prompt Editor */}
      {showPromptEditor && promptBaseData && (
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <Edit3 className="h-5 w-5" />
              <span>Prompt Base</span>
            </h3>
          </div>

          {/* Display no_content */}
          {/* {promptBaseData.no_content && (
            <div className="space-y-4 mb-4">
              <label htmlFor="noContentEditor" className="block text-sm font-medium text-gray-700 mb-2">
                No Content
              </label>
              <textarea
                id="noContentEditor"
                value={editedNoContent}
                onChange={(e) => setEditedNoContent(e.target.value)}
                className="input-field h-32 resize-none font-mono text-sm"
                placeholder="No content text will appear here..."
              />
            </div>
          )} */}

          {/* System Prompt editor */}
          <div className="space-y-4">
            <div>
              <label htmlFor="promptEditor" className="block text-sm font-medium text-gray-700 mb-2">
                System Prompt
              </label>
              <textarea
                id="promptEditor"
                value={editedSystemPrompt}
                onChange={(e) => setEditedSystemPrompt(e.target.value)}
                className="input-field h-64 resize-none font-mono text-sm"
                placeholder="System prompt will appear here..."
              />
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span>Characters: {editedSystemPrompt.length}</span>
              </div>
              <button
                onClick={handleSubmitPrompt}
                disabled={!editedSystemPrompt.trim() || isSubmittingPrompt || isOperationInProgress}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingPrompt ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Submit Prompt</span>
                  </>
                )}
              </button>
              {submitStatus && (
                <div
                  className={`mt-4 p-3 rounded-md text-sm ${
                    submitStatus === "success"
                      ? "bg-green-50 text-green-800 border border-green-200"
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  {submitMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Ask Form */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <MessageCircle className="h-5 w-5" />
            <span>Ask AI Assistant</span>
          </h3>
          <button
            onClick={
              async () => {
                await refreshCurrentContext();
                await handleGetPromptBase();
              }}
            className="btn-secondary flex items-center space-x-2"
            title="Refresh context data"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
        
        <form className="space-y-4">
          <div>
            <label htmlFor="askQuery" className="block text-sm font-medium text-gray-700 mb-2">
              Question
            </label>
            <textarea
              id="askQuery"
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder="Ask a question about your documents..."
              className="input-field h-24 resize-none"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="askTopK" className="block text-sm font-medium text-gray-700 mb-2">
                Top K Results
              </label>
              <input
                id="askTopK"
                type="number"
                min="1"
                value={topK}
                onChange={(e) => setTopK(parseInt(e.target.value))}
                className="input-field"
                title="Number of source documents to retrieve"
              />
            </div>

            <div>
              <label htmlFor="askFileRetrievalK" className="block text-sm font-medium text-gray-700 mb-2">
                File Retrieval K
              </label>
              <input
                id="askFileRetrievalK"
                type="number"
                min="0"
                value={fileRetrievalK}
                onChange={(e) => setFileRetrievalK(parseInt(e.target.value))}
                className="input-field"
                title="Number of most relevant files to retrieve in first stage (0 = no cap, use every file)"
              />
            </div>

            <div>
              <label htmlFor="fileRetrievalK" className="block text-sm font-medium text-gray-700 mb-2">
                Context Window
              </label>
              <input
                id="contextWindow"
                type="number"
                min="0"
                max="50"
                value={contextWindow}
                onChange={(e) => setContextWindow(parseInt(e.target.value))}
                className="input-field"
                title="Number of previous and following chunks to be found near relavent chunks"
              />
            </div>

            <div>
              <label htmlFor="askAlpha" className="block text-sm font-medium text-gray-700 mb-2">
                Alpha (0.0 - 1.0)
              </label>
              <input
                id="askAlpha"
                type="number"
                min="0.0"
                max="1.0"
                step="0.1"
                value={alpha}
                onChange={(e) => setAlpha(parseFloat(e.target.value))}
                className="input-field"
                title="Weight for combining different retrieval methods"
              />
            </div>

            <div>
              <label htmlFor="askScoreThreshold" className="block text-sm font-medium text-gray-700 mb-2">
                Score Threshold (cosine floor)
              </label>
              <input
                id="askScoreThreshold"
                type="number"
                min="0.0"
                max="1.0"
                step="0.05"
                value={scoreThreshold}
                onChange={(e) => setScoreThreshold(parseFloat(e.target.value))}
                className="input-field"
                title="Absolute cosine relevance floor; higher rejects more out-of-domain results"
              />
            </div>

            <div className="flex items-center pt-8">
              <input
                id="askUseReranker"
                type="checkbox"
                checked={useReranker}
                onChange={(e) => setUseReranker(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="askUseReranker" className="ml-2 text-sm font-medium text-gray-700"
                title="Second-stage cross-encoder reranking (bge-reranker-v2-m3); slower, off by default">
                Use reranker
              </label>
            </div>

            <div>
              <label htmlFor="askFileSimilarityThreshold" className="block text-sm font-medium text-gray-700 mb-2">
                File Similarity Threshold
              </label>
              <input
                id="askFileSimilarityThreshold"
                type="number"
                min="0.0"
                max="1.0"
                step="0.1"
                value={fileSimilarityThreshold}
                onChange={(e) => setFileSimilarityThreshold(parseFloat(e.target.value))}
                className="input-field"
                title="Minimum file similarity threshold for retrieval"
              />
            </div>
          </div>

          <div className="flex space-x-3">
            {/* <button
              type="submit"
              disabled={!queryText.trim() || isGeneratingPrompt || isOperationInProgress}
              className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPrompt ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Generating Prompt...</span>
                </>
              ) : (
                <>
                  <Edit3 className="h-4 w-4" />
                  <span>Generate Prompt</span>
                </>
              )}
            </button> */}
            
            {showPromptEditor && (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleAsk}
                  disabled={!queryText.trim() || isLoading || isOperationInProgress}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Asking...</span>
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4" />
                      <span>Ask</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Reset</span>
                </button>
              </div>
            )}
          </div>
        </form>

        {/* Error Display */}
        <ErrorDisplay
          error={error}
          onDismiss={() => setError(null)}
          title="Ask Error"
          variant="error"
        />

        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600">
            <strong>Current Context:</strong> {currentContext.contextId}
          </p>
          <p className="text-sm text-gray-600">
            <strong>RAG Items:</strong> {currentContext.ragItems?.length || 0}
          </p>
        </div>
      </div>

      


      {/* Ask Response */}
      {askResponse && (
        <div className="card">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <MessageCircle className="h-5 w-5" />
              <span>AI Response</span>
            </h3>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              {askResponse.language && (
                <div className="flex items-center space-x-1">
                  <Globe className="h-4 w-4" />
                  <span>{getLanguageFlag(askResponse.language)} {getLanguageDisplayName(askResponse.language)}</span>
                </div>
              )}
              {duration && (
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4" />
                  <span>{duration}ms</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Question:</strong> {askResponse.user_query}
            </p>
          </div>

          <div className="prose max-w-none">
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="whitespace-pre-wrap text-gray-900">
                {askResponse.answer}
              </div>
            </div>
          </div>

          {/* Sources */}
          {Array.isArray(askResponse.sources) && askResponse.sources.length > 0 ? (
            <div className="mt-6">
              <h4 className="text-md font-semibold mb-3 flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Sources ({askResponse.sources.length})</span>
              </h4>

              <div className="space-y-3">
                {askResponse.sources.map((source, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Source {index + 1}
                      </span>
                      <div className="flex flex-col items-end space-y-1">
                        {source.score !== undefined && (
                          <span className="text-sm text-gray-500">
                            Score: {source.score.toFixed(3)}
                          </span>
                        )}
                        {source.file_similarity !== undefined && (
                          <span className="text-sm text-blue-600 font-medium">
                            File Similarity: {source.file_similarity.toFixed(3)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* File Information */}
                    {source.file_name && (
                      <div className="mb-3 p-2 bg-blue-50 rounded-md">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-blue-800">📄 File:</span>
                          <span className="text-sm text-blue-700 font-medium">{source.file_name}</span>
                          {source.rag_id && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                              ID: {source.rag_id}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <p className="text-gray-900 mb-3">{source.knowledge}</p>

                    {/* Source and Index Information */}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                      {source.source && (
                        <div className="flex items-center space-x-1">
                          <span className="font-medium">Source:</span>
                          <span
                            className="truncate max-w-48"
                            title={source.source}
                          >
                            {source.source.split('/').pop() || source.source}
                          </span>
                        </div>
                      )}
                      {source.index !== undefined && source.index !== '' && (
                        <div className="flex items-center space-x-1">
                          <span className="font-medium">Page/Index:</span>
                          <span>{source.index}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-6 text-sm text-gray-500 italic">
              ⚠️ No sources available.
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default AskTab;
